import React, { useState, useEffect } from 'react';
import { MenuItem, TextField, Grid, Button } from '@mui/material';
import { useUser } from '../../contexts/UserContext';
import ChildrenAccordion from '../children/ChildrenAccordion';
import CustomSnackbar from '../elements/CustomSnackbar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { CHILD_DOB_KEY } from '../../constants';

interface ChildrenFormProps {
    onChildrenDataChange?: (data: any[]) => void;
}

const ChildrenForm: React.FC<ChildrenFormProps> = ({ onChildrenDataChange }) => {
    const { user } = useUser();
    const [children, setChildren] = useState(user?.children || []);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [childData, setChildData] = useState({
        fname: '',
        lname: '',
        dob: '',
        gender: '',
        isNew: true,
    });
    const maxYearOld = dayjs().subtract(17, 'year');

    const genders = ['Male', 'Female', 'Other'];

    useEffect(() => {
        if (user?.children) {
            setChildren(user.children);
        }
    }, [user]);

    useEffect(() => {
        if (onChildrenDataChange) {
            onChildrenDataChange(children);
        }
    }, [children, onChildrenDataChange]);

    const handleChildDataChange = (key: string, value: string | Dayjs | null) => {
        if (key === CHILD_DOB_KEY) {
            const formattedDate = value ? (typeof value === 'string' ? value : value.format('MM/DD/YYYY')) : '';
            setChildData(prevData => ({
                ...prevData,
                [key]: formattedDate,
            }));
        } else {
            setChildData(prevData => ({
                ...prevData,
                [key]: value,
            }));
        }

        return;
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleAddChild = () => {
        if (!childData.fname || !childData.lname || !childData.dob || !childData.gender) {
            setSnackbar({ open: true, message: "All fields are required", severity: 'error' });
            return;
        }

        if (dayjs(childData.dob, 'MM/DD/YYYY').isValid()) {
            const dob = dayjs(childData.dob, 'MM/DD/YYYY');
            if (dob < maxYearOld) {
                setSnackbar({ open: true, message: 'Date of Birth must be no more than 17 years old.', severity: 'error' });
                return;
            }
        } else {
            setSnackbar({ open: true, message: "All fields are required", severity: 'error' });
            return;
        }

        // Check for duplicate child
        const isDuplicate = children.some(
            (child) =>
                child.fname.toLowerCase() === childData.fname.toLowerCase() &&
                child.lname.toLowerCase() === childData.lname.toLowerCase() &&
                child.dob === childData.dob
        );

        if (isDuplicate) {
            setSnackbar({ open: true, message: 'A child with the same name and date of birth already exists', severity: 'error' });
            return;
        }

        const newChild = {
            id: Date.now(),
            ...childData,
            gender: childData.gender.toLowerCase(),
            isNew: true,
        };

        setChildren(currentChildren => [...currentChildren, newChild]);

        setChildData({
            fname: '',
            lname: '',
            dob: '',
            gender: '',
            isNew: true,
        });
    };

    const handleDeleteChild = (childId: number) => {
        setChildren(currentChildren => currentChildren.filter(child => child.id !== childId));
    };

    return (
        <Grid container spacing={2} className="text-left pt-5 single-form">
            <Grid item xs={12} md={3}></Grid>
            <Grid item xs={12} md={2}>
                <h2>Child Profile</h2>
            </Grid>
            <Grid item xs={12} md={2.7}>
                {children.map(child => (
                    <ChildrenAccordion
                        key={child.id}
                        {...child}
                        genders={genders}
                        onDelete={() => handleDeleteChild(child.id)}
                    />
                ))}
                <TextField
                    required
                    label="Child First Name"
                    variant="standard"
                    value={childData.fname}
                    onChange={(e) => handleChildDataChange('fname', e.target.value)}
                    inputProps={{ maxLength: 30 }}
                />
                <TextField
                    required
                    label="Child Last Name"
                    variant="standard"
                    value={childData.lname}
                    onChange={(e) => handleChildDataChange('lname', e.target.value)}
                    inputProps={{ maxLength: 30 }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Date of Birth"
                        value={childData.dob && dayjs(childData.dob, 'MM/DD/YYYY').isValid() ? dayjs(childData.dob, 'MM/DD/YYYY') : null}
                        onChange={(date) => handleChildDataChange('dob', date)}
                        format="MM/DD/YYYY"
                        slotProps={{ textField: { variant: 'standard' } }}
                        minDate={maxYearOld}
                        onError={(reason) => {
                            if (reason === 'minDate') {
                                console.error('Date of Birth must be no more than 17 years old.');
                            }
                        }}
                    />
                </LocalizationProvider>
                <TextField
                    required
                    select
                    label="Child Gender"
                    variant="standard"
                    value={childData.gender}
                    onChange={(e) => handleChildDataChange('gender', e.target.value)}
                >
                    {genders.map(option => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <div className="mt-2">
                    <Button className="add-child-button" disabled={!childData.fname || !childData.lname || !childData.dob || !childData.gender} onClick={handleAddChild}>+ Add Child</Button>
                </div>
            </Grid>
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                handleClose={handleCloseSnackbar}
            />
        </Grid>
    );
};

export default ChildrenForm;
